import type { ReactElement } from "react"
import { useCallback, useEffect, useState } from "react"
import { Link } from "gatsby"
import _t from "@core/i18n"
import useCookieStorage from "~/hooks/useCookieStorage"
import useSiteMetadataQuery from "~/hooks/useSiteMetadataQuery"
import { getBrand } from "~/lib/config"
import isExternalUrl from "~/lib/isExternalUrl"
import isServer from "~/lib/isServer"

const ACTION_CLASSES =
  "border-0 border-b-2 border-solid border-orange-500 bg-transparent p-0 pb-0.5 text-sm font-[700] uppercase text-white no-underline"

export default function Cookie(): ReactElement | null {
  const metadata = useSiteMetadataQuery()
  const cookieTermsLink = metadata.cookiesSiteUrl
  const { value, setValue } = useCookieStorage("cookieInfo", 365)
  const [visible, setVisible] = useState<boolean>(false)

  useEffect(() => {
    if (!isServer()) {
      setVisible(true)
    }
  }, [])

  const handleClick = useCallback(() => {
    setValue(`${Math.floor(Date.now() / 1000)}`)
  }, [setValue])

  if (getBrand() !== "strefa") {
    // Wyłączone na AZ COOKIES-47
    // Wyłączone na home COOKIES-51
    // Wyłączone na homecloud COOKIES-134
    // Wyłączone na ionos COOKIES-

    return null
  }

  if (!visible || value !== undefined) {
    return null
  }

  const isExternal = isExternalUrl(cookieTermsLink)

  return (
    <div className="fixed right-0 bottom-0 left-0 z-[1000] box-border bg-[#6c00f3] p-4 text-white md:left-auto md:right-4 md:bottom-4 md:max-w-[28rem] md:rounded-md md:shadow-lg">
      <div className="flex items-center justify-between gap-4">
        <p className="m-0 text-lg font-semibold text-white">
          {_t("cookie.title")}
        </p>

        <button
          type="button"
          aria-label={_t("cookie.close")}
          className="cursor-pointer border-0 bg-transparent p-0 text-white"
          onClick={handleClick}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 16 16"
            className="block h-3.5 w-3.5 fill-white"
          >
            <path d="M14.692 2.795a1.051 1.051 0 1 0-1.487-1.487L8 6.513 2.795 1.308a1.052 1.052 0 0 0-1.487 1.487L6.513 8l-5.205 5.205a1.052 1.052 0 1 0 1.487 1.487L8 9.487l5.205 5.205a1.051 1.051 0 1 0 1.487-1.487L9.487 8l5.205-5.205Z" />
          </svg>
        </button>
      </div>

      <p className="mt-4 mb-6 text-sm leading-relaxed text-white">
        {_t("cookie.message")}
      </p>

      <div className="flex flex-wrap items-center gap-6">
        <button
          type="button"
          className={`${ACTION_CLASSES} cursor-pointer`}
          onClick={handleClick}
        >
          {_t("cookie.ok")}
        </button>

        {isExternal ? (
          <a
            href={cookieTermsLink}
            rel="noopener noreferrer"
            title={_t("cookie.learnMore")}
            className={ACTION_CLASSES}
            onClick={handleClick}
          >
            {_t("cookie.learnMore")}
          </a>
        ) : (
          <Link
            to={cookieTermsLink}
            title={_t("cookie.learnMore")}
            className={ACTION_CLASSES}
            onClick={handleClick}
          >
            {_t("cookie.learnMore")}
          </Link>
        )}
      </div>
    </div>
  )
}
